import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { selectedSearchResultAtom, searchCondAtom } from '../../store/insuranceSearchStore';
import SolicitorHeader from '../../components/headers/SolicitorHeader';
import SearchSidebar from '../../components/SearchSidebar';
import SearchResults from '../../components/SearchResults';
import FeedbackPopup from '../../components/popups/FeedbackPopup';
import BadReasonFeedbackPopup from '../../components/popups/BadReasonFeedbackPopup';
import { fetchInsuranceCompanies, fetchAgencyInsuranceCompanies } from '../../services/api/InsuranceService';
import { searchHikiuke, searchInsurance, getSearchHistoryById } from '../../services/api/AiSearchService';
import { fetchDocumentTypesAccess } from '../../services/api/DocumentTypeService';
import { trackEvent, trackGoodFeedback, trackBadFeedback } from '../../services/api/TrackingService';
import { isDateRangeApplicable } from '../../utils/documentTypeUtils';
import iconTitle from '../../assets/img/icon-square.svg';
import '../../styles/AccountCreate.css';
import '../../styles/Tabs.css';
import '../../styles/button.css';
import '../../styles/InsuranceApplicationList.css';
import '../../styles/InsuranceSystemSearch.css';
import '../../styles/SearchTabs.css';
import { v4 as uuidv4 } from 'uuid';
import useRequireAuth from '../../hooks/useRequireAuth';
import { DOCUMENT_TYPE, getInsuranceTypeFromTabName, getInsuranceTypeName, getInsuranceTypeTabName, getProductTypeOptions } from '../../utils/insuranceTypeUtils';
import HikiukeSearchResults from '../../components/HikiukeSearchResults';
import { HIKIUKE_TAB_LIMIT, parsePathString } from '../../utils/insuranceSearchUtil';
import { openDetail, openHikiukeDetail } from '../../utils/insuranceSearchUtil';
import { getDefaultDocumentTypeCode, getOrderedDocumentTypes, normalizeDocumentTypes, hasEnabledNonDocumentTypeAll, getCompaniesForDocumentTypeAll } from '../../utils/documentTypeUtils';
import { isFeatureEnabledDocumentTypeAll } from '../../utils/featureFlags';
import { formatDateForDisplay } from '../../utils/dateFormatter';
import { normalizeToArray, sleep, normalizeSpaces } from '../../utils/common';

// 現在の日付をYYYY/MM/DD形式で取得する関数
const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

const InsuranceSearchPage = () => {
  const [dateFrom, setDateFrom] = useState(getTodayDate());
  const [dateTo, setDateTo] = useState(getTodayDate());
  const [searchQuery, setSearchQuery] = useState('');

  // フィードバックポップアップの状態管理を修正
  const [currentPopup, setCurrentPopup] = useState(null); // 'feedback' | 'badReason' | null
  const [isFeedbackSubmitting, setIsFeedbackSubmitting] = useState(false);

  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarMinimized, setSidebarMinimized] = useState(false);
  const [sidebarActive, setSidebarActive] = useState(false);
  const [isBulkButtonFixed, setIsBulkButtonFixed] = useState(false);
  const [documentTypeCode, setDocumentTypeCode] = useState('');
  const [selectedProductTypes, setSelectedProductTypes] = useState([]);
  const [selectedInsuranceCompanies, setSelectedInsuranceCompanies] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [hikiukeSearchResults, setHikiukeSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  // 権限に応じたアクセス可能な文書種別一覧
  const [documentTypesAccess, setDocumentTypesAccess] = useState([]);
  const [, setError] = useState(null);

  // Store all insurance companies
  const [allInsuranceCompanies, setAllInsuranceCompanies] = useState([]);
  // Store agency's insurance companies with approved_document_types
  const [agencyInsuranceCompanies, setAgencyInsuranceCompanies] = useState([]);
  // Display insurance companies based on documentType and admin status
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  // Map of company name to full company info
  const [companyInfoMap, setCompanyInfoMap] = useState(new Map());

  const [, setSelectedSearchResult] = useAtom(selectedSearchResultAtom);
  const [currentQueryId, setCurrentQueryId] = useState(null);

  const [activeTab, setActiveTab] = useState('life'); // 'life' | 'sonpo'

  const [searchCond, setSearchCond] = useAtom(searchCondAtom);

  const userInfo = useRequireAuth();

  const [selectedHikiukeResults, setSelectedHikiukeResults] = useState([]);
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [pendingRestoreCompanyIndexes, setPendingRestoreCompanyIndexes] = useState([]);
  const [appliedHistoryId, setAppliedHistoryId] = useState(null);
  const [shouldAutoSearch, setShouldAutoSearch] = useState(false);
  const [searchExecutionSignal, setSearchExecutionSignal] = useState(0);

  // アクセス可否を踏まえて文書種別情報を解決
  const getSelectedDocumentType = (code, insuranceType) => {
    const selectedInsuranceType = Number(insuranceType ?? '');
    return (
      documentTypesAccess.find((type) => {
        const typeInsurance = Number(type.insurance_type ?? type.insuranceType ?? '');
        return type.code === code && typeInsurance === selectedInsuranceType;
      }) || documentTypesAccess.find((type) => type.code === code)
    );
  };

  // タブの保険種別に応じた文書種別オプション
  const getDocumentTypeOptions = () => {
    const insuranceType = getInsuranceTypeFromTabName(activeTab);
    const options = getOrderedDocumentTypes(documentTypesAccess, insuranceType);
    // DocumentTypeAll フラグが有効な場合のみ「すべて」エントリを合成して先頭に追加
    // バックエンドは all を返さないため、フロントエンドで合成する
    if (!isFeatureEnabledDocumentTypeAll()) {
      return options;
    }
    const allEntry = {
      code: DOCUMENT_TYPE.ALL,
      name: 'すべて',
      insurance_type: insuranceType,
      // ALL は「引受目安以外に有効な文書種別が1つ以上ある場合」のみ有効
      is_enabled: hasEnabledNonDocumentTypeAll(options)
    };
    return [allEntry, ...options];
  };

  // 現在のタブで利用可能な文書種別があるかチェック
  const hasAvailableDocumentTypes = () => {
    const options = getDocumentTypeOptions();
    return options.some(option => option.is_enabled);
  };

  // 指定した文書種別コードが利用可能かを判定
  const isDocumentTypeAvailable = (code) => {
    const options = getDocumentTypeOptions();
    if (code === DOCUMENT_TYPE.ALL) {
      // 「すべて」は他に有効な文書種別が1つ以上あるかで判定
      return hasEnabledNonDocumentTypeAll(options);
    }
    // 個別の文書種別はそのエントリ自体の is_enabled で判定
    const insuranceType = getInsuranceTypeFromTabName(activeTab);
    const selected = getSelectedDocumentType(code, insuranceType);
    return !selected || selected.is_enabled;
  };

  // 全ての保険会社情報をもとに、保険会社情報マップを更新
  const updateCompanyInfoMap = () => {
    if (allInsuranceCompanies.length > 0) {
      const infoMap = new Map(
        allInsuranceCompanies.map(company => [company.name, company])
      );
      setCompanyInfoMap(infoMap);
    }
  };

  // 全保険会社情報が更新されたらマップを更新
  useEffect(updateCompanyInfoMap, [allInsuranceCompanies]);

  // フィードバックポップアップのハンドラー関数を追加
  const handleFeedbackClose = () => {
    setCurrentPopup(null);
  };

  const handleGoodFeedback = async () => {
    if (isFeedbackSubmitting) return;

    setIsFeedbackSubmitting(true);

    try {
      await trackGoodFeedback(currentQueryId, '検索結果一覧');
      console.log('Good feedback submitted');
      setCurrentPopup(null);
    } catch (error) {
      console.error('フィードバック送信エラー:', error);
    } finally {
      setIsFeedbackSubmitting(false);
    }
  };

  const handleBadFeedback = () => {
    // badボタン押下時、BadReasonFeedbackPopupに切り替え
    if (isFeedbackSubmitting) return;
    setCurrentPopup('badReason');
  };

  // Badフィードバック送信処理
  const handleFeedbackSubmit = async (feedbackData) => {
    if (isFeedbackSubmitting) return;

    setIsFeedbackSubmitting(true);

    try {
      await trackBadFeedback(currentQueryId, '検索結果一覧', feedbackData);
      console.log('Bad feedback submitted:', feedbackData);
    } catch (error) {
      console.error('フィードバック送信エラー:', error);
    } finally {
      setIsFeedbackSubmitting(false);
    }
  };

  // 戻るハンドラー
  const handleFeedbackBack = () => {
    setCurrentPopup('feedback');
  };

  // Screen size monitoring to update mobile detection
  // アクセス可能な文書種別を取得（検索条件の制御に使用）
  useEffect(() => {
    const checkIsMobile = () => {
      const newIsMobile = window.innerWidth < 768; // 768px未満をモバイルと判断
      setIsMobile(newIsMobile);

      if (newIsMobile) {
        // 768px未満の場合、サイドバーを非表示に
        setSidebarVisible(false);
      } else {
        // 768px以上の場合、サイドバーを表示状態にリセット
        setSidebarVisible(true);
        setSidebarActive(false); // モバイル用のアクティブ状態もリセット
      }
    };

    // Initial check
    checkIsMobile();

    // Check on resize
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // クエリパラメータのhistoryIdから検索条件を復元
  useEffect(() => {
    const historyId = urlSearchParams.get('historyId');
    if (historyId) {
      // クエリパラメータからhistoryIdを削除してURLをクリーンアップ
      const nextParams = new URLSearchParams(urlSearchParams);
      nextParams.delete('historyId');
      setAppliedHistoryId(historyId); // 復元処理の対象IDを状態に保存
      setShouldAutoSearch(false);
      setUrlSearchParams(nextParams, { replace: true });
      return;
    }
    // 復元対象IDがない場合は何もしない
    if (!appliedHistoryId) return;

    /**
     * 検索条件の復元処理
     * @returns {Promise<void>}
     */
    const restoreByHistoryId = async () => {
      try {
        await sleep(500); // URLクリーンアップ後に少し待機（必要に応じて調整）

        // クエリパラメータのhistoryIdをもとに、サーバーから検索条件の取得を行う
        const history = (await getSearchHistoryById(appliedHistoryId)).search_params;

        // 取得した検索条件をもとに、各状態を復元する
        // 保険種別
        const insuranceType = Number(history?.insurance_type ?? 1);
        setActiveTab(getInsuranceTypeTabName(insuranceType));

        // 文書種別
        const restoredDocumentType = history?.document_type_code ?? '';
        if (restoredDocumentType) {
          setDocumentTypeCode(restoredDocumentType);
        }

        // 保険商品種類
        // クエリパラメータから復元した保険商品種類が、現在の保険種別の選択肢に存在するか確認してから状態にセットする
        const options = getProductTypeOptions(insuranceType);
        const productTypes = normalizeToArray(history?.categories).filter(type => options.includes(type));
        setSelectedProductTypes(productTypes);

        // その他の検索条件
        setSearchQuery(history?.query ?? '');
        setDateFrom(formatDateForDisplay(history?.start_date ?? ''));
        setDateTo(formatDateForDisplay(history?.end_date ?? ''));

        setSelectedInsuranceCompanies([]);
        const restoredCompanyIndexes = normalizeToArray(history?.companies);
        setPendingRestoreCompanyIndexes(restoredCompanyIndexes);

        // 復元対象の保険会社がない場合は、復元完了後に自動検索を実行するフラグを立てる
        // 復元対象がある場合は、保険会社候補の取得後に選択状態を復元する処理が走るため、そちらで自動検索のフラグを立てる
        if (restoredCompanyIndexes.length === 0) {
          setShouldAutoSearch(true);
        }
      } catch (error) {
        // エラーダイアログ表示はBaseApiServiceで行う
        // エラーコンソール出力は各ApiServiceで行う
      }
      finally {
        // 復元処理が完了したら、appliedHistoryIdをリセットして再度復元処理が走らないようにする
        setAppliedHistoryId(null);
      }
    };

    restoreByHistoryId();
  }, [urlSearchParams, appliedHistoryId, setUrlSearchParams]);

  // 保険会社候補の取得後に、復元対象会社を突合して選択状態を復元
  useEffect(() => {
    if (pendingRestoreCompanyIndexes.length === 0) return;
    if (isLoadingCompanies) return;
    if (insuranceCompanies.length === 0) return;

    const restoredCompanies = insuranceCompanies.filter(company =>
      pendingRestoreCompanyIndexes.includes(company.indexName)
    );

    setSelectedInsuranceCompanies(restoredCompanies);
    setPendingRestoreCompanyIndexes([]);
    setShouldAutoSearch(true);
  }, [insuranceCompanies, pendingRestoreCompanyIndexes, isLoadingCompanies]);

  // 検索履歴復元後、条件反映が完了したタイミングで自動検索を1回だけ実行
  useEffect(() => {
    if (!shouldAutoSearch) return;
    if (isLoadingCompanies) return;
    if (pendingRestoreCompanyIndexes.length > 0) return;
    if (documentTypesAccess.length === 0) return;

    setShouldAutoSearch(false);
    handleSearch();
  }, [shouldAutoSearch, isLoadingCompanies, pendingRestoreCompanyIndexes, documentTypesAccess]);

  // 選択中の文書種別が無効なら有効なデフォルトに補正
  useEffect(() => {
    const fetchAccessDocumentTypes = async () => {
      if (!userInfo) {
        return;
      }
      try {
        const response = await fetchDocumentTypesAccess();
        setDocumentTypesAccess(normalizeDocumentTypes(response));
      } catch (error) {
        // エラーダイアログ表示はBaseApiServiceで行う
        // エラーコンソール出力は各ApiServiceで行う
      }
    };

    fetchAccessDocumentTypes();
  }, [userInfo]);

  useEffect(() => {
    const insuranceType = getInsuranceTypeFromTabName(activeTab);
    const options = getDocumentTypeOptions();
    if (options.length === 0) {
      return;
    }

    const selected = options.find((type) => type.code === documentTypeCode);
    const defaultCode = getDefaultDocumentTypeCode(documentTypesAccess, insuranceType);
    if (!selected || !selected.is_enabled) {
      setDocumentTypeCode(defaultCode);
    }
  }, [documentTypesAccess, activeTab]);

  // 文書種別/権限に応じて保険会社一覧を取得
  useEffect(() => {
    const fetchSetInsuranceCompanies = async () => {
      setIsLoadingCompanies(true);
      try {
        if (!userInfo) {
          return;
        }

        const isAdmin = userInfo.is_admin;
        // 管理者で募集人サイトログイン時のみ、引受目安・ハンドブックを絞り込み
        // しおり・約款 / 新契約関連 / 保全・収納・支払関連 は全社表示
        const docTypeKey = mapDocumentTypeToKey(documentTypeCode);
        const shouldFilterForAdmin = isAdmin && (docTypeKey === 'hikiuke' || docTypeKey === 'handbook');
        const insuranceCompanyQuery = shouldFilterForAdmin
          ? { document_types: ['hikiuke', 'handbook'] }
          : {};

        // Fetch all insurance companies (adminはhikiuke/handbook登録済みの会社のみ)
        const insuranceCompaniesResponse = await fetchInsuranceCompanies(insuranceCompanyQuery);
        const allCompanies = insuranceCompaniesResponse.map(company => ({
          name: company.name,
          nameKana: company.name_kana,
          indexName: company.index_name,
          insuranceType: company.insurance_type
        }));

        setAllInsuranceCompanies(allCompanies);

        // 管理者は全件表示
        if (isAdmin) {
          setInsuranceCompanies(allCompanies);
        } else {
          // 代理店は許可された文書種別（approved_document_types）に基づいて絞り込み
          const agencyId = userInfo.agency_id;
          const agencyInsuranceCompaniesResponse = await fetchAgencyInsuranceCompanies(agencyId);

          // 承認済み文書種別を付与して保持
          const agencyCompaniesWithTypes = agencyInsuranceCompaniesResponse.map(agencyCompany => {
            // 保険会社マスタ情報と紐付け
            const fullCompanyInfo = insuranceCompaniesResponse.find(company => company.id === agencyCompany.id);
            return {
              id: agencyCompany.id,
              name: fullCompanyInfo.name,
              nameKana: fullCompanyInfo.name_kana,
              indexName: fullCompanyInfo.index_name,
              insuranceType: fullCompanyInfo.insurance_type,
              approved_document_types: agencyCompany.approved_document_types
            };
          });

          setAgencyInsuranceCompanies(agencyCompaniesWithTypes);

          // For initial load, set companies based on documentType
          if (documentTypeCode === DOCUMENT_TYPE.POLICY_BOOKLET) {
            setInsuranceCompanies(allCompanies);
          } else if (documentTypeCode === DOCUMENT_TYPE.ALL) {
            // 「すべて」は documentTypesAccess の確定を待って変更ハンドラ effect で正しくフィルタリングするため、ここでは空にしておく
            setInsuranceCompanies([]);
          } else {
            // Apply filtering based on document type
            const filteredCompanies = getFilteredCompaniesByDocumentType(documentTypeCode, agencyCompaniesWithTypes);
            setInsuranceCompanies(filteredCompanies);
          }
        }
      } catch (error) {
        // エラーダイアログ表示はBaseApiServiceで行う
        // エラーコンソール出力は各ApiServiceで行う
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    fetchSetInsuranceCompanies();
  }, [userInfo, documentTypeCode]);

  // Helper function to filter companies by document type
  const getFilteredCompaniesByDocumentType = (docType, agencyCompanies) => {
    const documentTypeKey = mapDocumentTypeToKey(docType);

    if (!documentTypeKey) {
      return [];
    }

    // 代理店が許可された文書種別のみ残す
    return agencyCompanies
      .filter(company => company.approved_document_types.includes(documentTypeKey))
      .map(company => ({
        name: company.name,
        nameKana: company.nameKana,
        indexName: company.indexName,
        insuranceType: company.insuranceType
      }));
  };

  // ドキュメント種別コードの検証
  const mapDocumentTypeToKey = (docType) => {
    if (!docType) return '';
    const allowedCodes = new Set(Object.values(DOCUMENT_TYPE));
    return allowedCodes.has(docType) ? docType : '';
  };

  // Update displayed insurance companies when documentType changes
  useEffect(() => {
    if (!userInfo) return;
    const isAdmin = userInfo.is_admin;

    // Admin users always see all companies
    if (isAdmin) {
      return;
    }

    // 代理店は文書種別ごとに表示対象を切り替える
    if (documentTypeCode === DOCUMENT_TYPE.POLICY_BOOKLET) {
      setInsuranceCompanies(allInsuranceCompanies);
      return;
    }

    let filteredCompanies;
    if (documentTypeCode === DOCUMENT_TYPE.ALL) {
      // 「すべて」選択時は documentTypesAccess をもとに表示対象を決定する
      const insuranceType = getInsuranceTypeFromTabName(activeTab);
      filteredCompanies = getCompaniesForDocumentTypeAll({
        documentTypesAccess,
        agencyInsuranceCompanies,
        allInsuranceCompanies,
        insuranceType
      });
    } else {
      filteredCompanies = getFilteredCompaniesByDocumentType(documentTypeCode, agencyInsuranceCompanies);
    }

    setInsuranceCompanies(filteredCompanies);

    // Filter out selected companies that are no longer available
    const availableCompanyNames = filteredCompanies.map(company => company.indexName);
    const filteredSelection = selectedInsuranceCompanies.filter(
      selectedCompany => availableCompanyNames.includes(selectedCompany.indexName)
    );

    if (filteredSelection.length !== selectedInsuranceCompanies.length) {
      setSelectedInsuranceCompanies(filteredSelection);
    }
  // documentTypesAccess と activeTab を依存配列に追加:
  // - documentTypesAccess: APIロード完了時に ALL の保険会社フィルタを正しく再計算するため
  // - activeTab: タブ切替時に保険種別に合わせた再フィルタが必要なため
  }, [documentTypeCode, allInsuranceCompanies, agencyInsuranceCompanies, documentTypesAccess, activeTab]);

  // Toggle sidebar visibility/hiding - full toggle
  const toggleSidebar = () => {
    if (isMobile) {
      // On mobile, toggle active state (slide up/down)
      setSidebarActive(!sidebarActive);
    } else {
      setSidebarVisible(!sidebarVisible);
      // When showing sidebar, also un-minimize it
      if (!sidebarVisible) {
        setSidebarMinimized(false);
      }
    }
  };

  // Toggle sidebar minimized/maximized state
  const toggleSidebarMinimized = () => {
    setSidebarMinimized(!sidebarMinimized);
    // Always keep visible
    setSidebarVisible(true);
  };

  // Navigate to detail page when a card is clicked
  const handleCardClick = (result) => {
    if (!result) return;
    openDetail(result, getInsuranceTypeFromTabName(activeTab), setSelectedSearchResult);
  };

  // 検索パラメータのバリデーション関数
  const validateSearchParams = (query, _productTypes, insuranceCompanies, documentTypeCode, dateFrom, dateTo, _insuranceType) => {
    const errors = [];

    // 日付のバリデーション（しおり・約款、または全文書種別の場合）
    if (isDateRangeApplicable(documentTypeCode) && dateFrom && dateTo) {
      // 日付文字列をDateオブジェクトに変換
      const fromDate = new Date(dateFrom.replace(/\//g, '-'));
      const toDate = new Date(dateTo.replace(/\//g, '-'));

      // 日付の有効性をチェック
      if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
        errors.push('・日付の形式が正しくありません。正しい日付を入力してください。');
      } else if (fromDate > toDate) {
        // 開始日が終了日より後の場合
        errors.push('・開始日は終了日以前の日付を選択してください。');
      }
    }

    // 各入力項目のチェック
    const isQueryEmpty = !query.trim();
    const isInsuranceCompaniesEmpty = insuranceCompanies.length === 0;

    if (query.length > 100) {
      errors.push('・検索キーワードは100文字以内で入力してください。');
    }

    // 個別の入力項目エラーをチェック
    if (isQueryEmpty) {
      if (documentTypeCode === DOCUMENT_TYPE.HIKIUKE) {
        errors.push('・傷病名を入力してください。');
      } else {
        errors.push('・検索キーワードを入力してください。');
      }
    }

    // 引受目安の場合、保険会社の選択は必須ではない
    if (documentTypeCode !== DOCUMENT_TYPE.HIKIUKE && isInsuranceCompaniesEmpty) {
      errors.push('・少なくとも1つの保険会社を選択してください。');
    }

    // エラーがある場合は改行で結合して返す
    if (errors.length > 0) {
      return '以下の項目を確認してください：\n\n' + errors.join('\n');
    }

    // エラーがない場合はnullを返す
    return null;
  };

  // Function to handle search submission
  // Function to handle search submission
  const handleSearch = async () => {
    // 予測変換と同じスペース正規化を行い、本検索との入力差異をなくす。
    const effectiveSearchQuery = normalizeSpaces(searchQuery).trim();

    // 検索ボタンが非活性の場合は処理を中断
    if (!hasAvailableDocumentTypes()) {
      return;
    }
    if (!isDocumentTypeAvailable(documentTypeCode)) {
      alert('選択した文書種別は利用できません');
      return;
    }

    // バリデーション関数を呼び出す
    const validationError = validateSearchParams(
      effectiveSearchQuery,
      selectedProductTypes,
      selectedInsuranceCompanies,
      documentTypeCode,
      dateFrom,
      dateTo,
      getInsuranceTypeFromTabName(activeTab)
    );

    // エラーがあればアラートを表示して処理を終了
    if (validationError) {
      alert(validationError);
      return;
    }

    setSearchExecutionSignal((prev) => prev + 1);
    setIsLoading(true);

    const queryId = uuidv4(); // 検索ごとの一意なID
    const companies = selectedInsuranceCompanies.length > 0
      ? selectedInsuranceCompanies.map(company => company.indexName)
      : null;
    const categories = documentTypeCode === DOCUMENT_TYPE.HIKIUKE ? [] : selectedProductTypes.length > 0 ? selectedProductTypes : [];
    const formattedDateFrom = isDateRangeApplicable(documentTypeCode) && dateFrom ? dateFrom.replace(/\//g, '-') : '';
    const formattedDateTo = isDateRangeApplicable(documentTypeCode) && dateTo ? dateTo.replace(/\//g, '-') : '';
    const documentTypeItem = getSelectedDocumentType(documentTypeCode, getInsuranceTypeFromTabName(activeTab));
    const documentTypeId = documentTypeItem?.id ?? null;

    const searchRequest = {
      query: effectiveSearchQuery,
      // 文書種別が未選択の場合はパラメータに含めない
      ...(documentTypeCode ? {
        classification: documentTypeCode,
        document_type_id: documentTypeId,
        document_type_code: documentTypeCode,
      } : {}),
      start_date: formattedDateFrom,
      end_date: formattedDateTo,
      companies: companies,
      categories: categories,
      insurance_type: getInsuranceTypeFromTabName(activeTab),
    };

    setSearchCond(searchRequest);

    // ✅ 検索条件をトラッキング
    trackEvent({
      name: "InsuranceSearchExecuted",
      properties: {
        queryId,
        query: effectiveSearchQuery,
        documentType: documentTypeCode,
        companies: JSON.stringify(companies),
        category: categories.join(','),
        dateFrom: formattedDateFrom,
        dateTo: formattedDateTo,
        insuranceType: getInsuranceTypeFromTabName(activeTab)
      }
    }).catch(error => console.error('検索実行ログ送信エラー:', error));

    setCurrentQueryId(queryId);

    try {
      if (documentTypeCode === DOCUMENT_TYPE.HIKIUKE) {
        // 引受目安専用処理
        const hikiukeResults = await searchHikiuke(searchRequest, queryId, allInsuranceCompanies);
        setSelectedHikiukeResults([]);
        setSearchResults([]);
        setHikiukeSearchResults(hikiukeResults);

        setError(null);
        setCurrentPopup('feedback');

        return;
      }

      const response = await searchInsurance(searchRequest);
      const formattedResults = formatSearchResults(response, companyInfoMap);

      // ✅ 各結果に queryId を追加（ネストあり）
      const enrichedResults = formattedResults.map(companyResult => ({
        ...companyResult,
        results: companyResult.results.map(r => ({ ...r, queryId }))
      }));

      // 検索結果をトラッキング
      trackEvent({
        name: "InsuranceSearchResult",
        properties: {
          queryId,
          resultJson: JSON.stringify(enrichedResults)
        }
      }).catch(error => console.error('検索結果ログ送信エラー:', error));

      setHikiukeSearchResults([]);
      setSearchResults(enrichedResults);
      // ✅ 成功時のみエラーをクリア
      setError(null);
      setCurrentPopup('feedback');
    } catch (error) {
      // エラーダイアログ表示はBaseApiServiceで行う
      // エラーコンソール出力は各ApiServiceで行う
      // ✅ 検索失敗をトラッキング
      trackEvent({
        name: "InsuranceSearchFailed",
        properties: {
          queryId,
          query: effectiveSearchQuery,
          documentType: documentTypeCode,
          companies: JSON.stringify(companies),
          category: selectedProductTypes.join(','),
          dateFrom: formattedDateFrom,
          dateTo: formattedDateTo,
          errorMessage: error?.message || 'unknown error',
          insuranceType: getInsuranceTypeFromTabName(activeTab)
        }
      }).catch(error => console.error('検索失敗ログ送信エラー:', error));
      // エラー時は検索結果の状態を変更しない
    } finally {
      setIsLoading(false);

      // モバイル端末の場合、検索完了後にサイドバーを最小化
      if (isMobile) {
        setSidebarActive(false);
      }
    }
  };

  // 選択された保険会社で結果オブジェクトを初期化する関数
  const initializeResultsByCompany = (selectedInsuranceCompanies, companyInfoMap) => {
    const resultsByCompany = {};

    selectedInsuranceCompanies.forEach(selectedCompany => {
      resultsByCompany[selectedCompany.name] = {
        id: selectedCompany.name,
        index: selectedCompany.indexName,
        company: selectedCompany.name,
        nameKana: companyInfoMap.get(selectedCompany.name)?.nameKana || selectedCompany.nameKana || selectedCompany.name,
        results: []
      };
    });

    return resultsByCompany;
  };

  // Helper function to format API results to match UI expected structure
  const formatSearchResults = (apiResults, companyInfoMap) => {
    // Group results by company
    const resultsByCompany = initializeResultsByCompany(selectedInsuranceCompanies, companyInfoMap);

    let idCounter = 0;

    // Process the search results from the API
    if (apiResults && apiResults.results) {
      apiResults.results.forEach(item => {
        const { company, title } = parsePathString(item.blob_name);

        if (!resultsByCompany[company]) {
          resultsByCompany[company] = {
            id: company,
            index: selectedInsuranceCompanies.filter(c => c.name === company)[0]?.indexName || '',
            company: company,
            nameKana: companyInfoMap.get(company)?.nameKana || company,
            results: []
          };
        }

        resultsByCompany[company].results.push({
          id: idCounter++, // 各結果に一意のIDを付与
          title: title || 'タイトルがありません',
          pages: item.pages,
          similarity: Math.round(item.score) || 0, // Convert score to percentage
          pdfUrl: item.blob_name || 'PDF URLがありません',
          summary: item.summary || '要約がありません',
          classification: `【${item.classification}】` || '分類がありません',
          classificationLabel: item.classification || '',
          productName: item.product_name || '商品名がありません',
          creationDate: item.creation_date || '作成日がありません',
        });
      });
    }

    // 保険会社の読み仮名でソート
    return Object.values(resultsByCompany).sort((a, b) =>
      a.nameKana.localeCompare(b.nameKana, 'ja')
    );
  };

  // 検索条件リセット処理
  const handleReset = () => {
    // 検索条件を初期値にリセット
    setDateFrom(getTodayDate());
    setDateTo(getTodayDate());
    setSearchQuery('');
    // デフォルト文書種別は getDefaultDocumentTypeCode に委ねる（フラグ制御を一元管理）
    const insuranceType = getInsuranceTypeFromTabName(activeTab);
    setDocumentTypeCode(getDefaultDocumentTypeCode(documentTypesAccess, insuranceType));
    setSelectedProductTypes([]);
    setSelectedInsuranceCompanies([]);
    console.log('検索条件をリセットしました');
  };

  // 一括表示ボタンの追従処理
  useEffect(() => {
    if (documentTypeCode !== DOCUMENT_TYPE.HIKIUKE) {
      setIsBulkButtonFixed(false);
      return;
    }
    const handleScroll = () => {
      const scrollTop = document.body.scrollTop || document.documentElement.scrollTop;
      setIsBulkButtonFixed(scrollTop > 100);
    };
    handleScroll();
    document.body.addEventListener('scroll', handleScroll);
    return () => {
      document.body.removeEventListener('scroll', handleScroll);
    };
  }, [documentTypeCode]);

  const handleTabChange = (tabName) => {
    handleReset();
    setActiveTab(tabName);
    setHikiukeSearchResults([]);
    setSelectedHikiukeResults([]);
    setSearchResults([]);
    setError(null);
  };

  const handleChangeDocumentType = (newDocumentType) => {
    // 利用不可な文書種別は変更しない
    if (!isDocumentTypeAvailable(newDocumentType)) {
      alert('選択した文書種別は利用できません');
      return;
    }
    // 引受目安に変更した場合、他の検索結果をクリア
    if (documentTypeCode !== DOCUMENT_TYPE.HIKIUKE && newDocumentType === DOCUMENT_TYPE.HIKIUKE) {
      setSearchResults([]);
    }
    // 引受目安から他のタイプに変更した場合、引受目安の検索結果と選択状態をクリア
    if (documentTypeCode === DOCUMENT_TYPE.HIKIUKE && newDocumentType !== DOCUMENT_TYPE.HIKIUKE) {
      setHikiukeSearchResults([]);
      setSelectedHikiukeResults([]);
    }
    setDocumentTypeCode(newDocumentType);
  };

  const batchShowDetail = async () => {
    if (selectedHikiukeResults.length === 0) {
      alert('一括確認する検索結果を選択してください。');
      return;
    }
    if (selectedHikiukeResults.length > HIKIUKE_TAB_LIMIT) {
      alert(`一括確認は最大${HIKIUKE_TAB_LIMIT}件まで選択可能です。`);
      return;
    }
    // 一括表示ボタン押下時の処理
    await openHikiukeDetail(selectedHikiukeResults, setSelectedSearchResult);
  };

  return (
    <div className="insurance-search-page">
      {/* SolicitorHeader */}
      <SolicitorHeader onMenuToggle={(isOpen) => {
        // Menu state change handler can be implemented here
      }} />

      {/* Mobile sidebar toggle button */}
      {isMobile && (
        <div className="sidebar-toggle-container">
          <button className="sidebar-toggle-button" onClick={toggleSidebar}>
            <span className="c-display-flex c-display-flex--gap10">
              <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="c-mr-10">
                <path d="M0 2H10.5" stroke="white" />
                <path d="M13.5 2H14.5" stroke="white" />
                <circle cx="12" cy="2" r="1.5" stroke="white" />
                <path d="M0 12L8.5 12" stroke="white" />
                <path d="M11.5 12L14.5 12" stroke="white" />
                <circle cx="10" cy="12" r="1.5" stroke="white" />
                <path d="M0 7L4 7" stroke="white" />
                <path d="M6.5 7H14.5" stroke="white" />
                <circle cx="5" cy="7" r="1.5" stroke="white" />
              </svg>
              {sidebarActive ? '閉じる' : '文書検索'}
            </span>
            <span className={`toggle-icon ${sidebarActive ? 'open' : ''}`}></span>
          </button>
        </div>
      )}

      {/* Main content */}
      <div className="page-container">
        <div className={`insurance-layout ${isMobile ? 'mobile' : ''}`}>
          {/* Left sidebar with visibility/state control */}
          {(sidebarVisible || isMobile) && (
            <SearchSidebar
              isMobile={isMobile}
              sidebarMinimized={sidebarMinimized}
              sidebarActive={sidebarActive}
              toggleSidebarMinimized={toggleSidebarMinimized}
              documentType={documentTypeCode}
              setDocumentType={handleChangeDocumentType}
              documentTypes={getDocumentTypeOptions()}
              selectedInsuranceCompanies={selectedInsuranceCompanies}
              setSelectedInsuranceCompanies={setSelectedInsuranceCompanies}
              selectedProductTypes={selectedProductTypes}
              setSelectedProductTypes={setSelectedProductTypes}
              dateFrom={dateFrom}
              setDateFrom={setDateFrom}
              dateTo={dateTo}
              setDateTo={setDateTo}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              onSearch={handleSearch}
              onReset={handleReset}
              insuranceCompanies={insuranceCompanies}
              isLoadingCompanies={isLoadingCompanies}
              isLoading={isLoading}
              searchExecutionSignal={searchExecutionSignal}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              isSearchDisabled={!hasAvailableDocumentTypes()}
            />
          )}

          {/* Search results main area */}
          <main className={`insurance-results ${isMobile || (!sidebarVisible || sidebarMinimized) ? 'fullwidth' : ''}`}>
            <div className={`page-header ${documentTypeCode === DOCUMENT_TYPE.HIKIUKE ? 'page-header--underwriting' : ''}`}>
              <div className={`title-section ${documentTypeCode === DOCUMENT_TYPE.HIKIUKE ? 'title-section--side-btn' : ''}`}>
                <div className="icon-square">
                  <img src={iconTitle} alt="タイトルアイコン" />
                </div>
                <h1>検索結果一覧 - {getInsuranceTypeName(getInsuranceTypeFromTabName(activeTab))}</h1>
              </div>
              {documentTypeCode === DOCUMENT_TYPE.HIKIUKE && (
                <div className={`view-btn-bulk-inner ${isBulkButtonFixed ? 'view-btn-bulk--container' : ''}`}>
                  <button className='view-btn-bulk' onClick={batchShowDetail}>一括確認</button>
                </div>
              )}
            </div>

            {documentTypeCode === DOCUMENT_TYPE.HIKIUKE && (
              <div className="guarded-message">
                <p className='guarded-message-text'>※原文に該当傷病名がない場合、AIが関連度の高い別の傷病名を推定して表示することがあります。正確な取り扱いや判断については、各保険会社へご確認ください。</p>
              </div>
            )}

            {/* Loading and error states */}
            {isLoading && (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>検索中...</p>
              </div>
            )}

            {/* Empty state */}
            {!isLoading && searchResults.length === 0 && hikiukeSearchResults.length === 0 && (
              <div className="empty-results">
                <p>検索結果がありません。検索条件を変更してください。</p>
              </div>
            )}

            {/* Search results content */}
            {documentTypeCode !== DOCUMENT_TYPE.HIKIUKE && !isLoading && searchResults.length > 0 && (
              <SearchResults
                searchResults={searchResults}
                handleCardClick={handleCardClick}
                isMobile={isMobile}
                searchCond={searchCond}
              />
            )}

            {documentTypeCode === DOCUMENT_TYPE.HIKIUKE && !isLoading && hikiukeSearchResults.length > 0 && (
              <HikiukeSearchResults
                searchResults={hikiukeSearchResults}
                selectedResults={selectedHikiukeResults}
                setSelectedResults={setSelectedHikiukeResults}
                isMobile={isMobile}
              />
            )}
          </main>
        </div>
      </div>

      {currentPopup === 'feedback' && (
        <FeedbackPopup
          onClose={handleFeedbackClose}
          handleGoodSubmit={handleGoodFeedback}
          handleBadSubmit={handleBadFeedback}
          isSubmitting={isFeedbackSubmitting}
        />
      )}

      {currentPopup === 'badReason' && (
        <BadReasonFeedbackPopup
          onClose={handleFeedbackClose}
          onBack={handleFeedbackBack}
          queryId={currentQueryId}
          handleSubmit={handleFeedbackSubmit}
          isSubmitting={isFeedbackSubmitting}
        />
      )}
    </div>
  );
};

export default InsuranceSearchPage;
